import { useState, useEffect, createContext, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, UserRound } from "lucide-react";
import { http } from "./api";
import { useNoIndex } from "../lib/seo";

const PortalCtx = createContext(null);
export const usePortalUser = () => useContext(PortalCtx);

export default function PortalLayout({ children }) {
  useNoIndex();
  const [account, setAccount] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    http.get("/portal/client/account")
      .then(({ data }) => setAccount(data))
      .catch(() => setAccount(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    navigate("/portal/login");
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-stone2 bg-ink" data-testid="portal-loading">Loading…</div>;
  if (!account) {
    navigate("/portal/login", { replace: true });
    return null;
  }

  return (
    <PortalCtx.Provider value={account}>
      <div className="min-h-screen bg-ink text-bone" data-testid="portal-shell">
        <header className="border-b border-line bg-surface1 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
            <Link to="/portal" className="flex items-center gap-3">
              <img src="/bigibila-logo.png" alt="Bigibila" className="h-9 w-9" />
              <span className="font-serif text-lg">Bigibila</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">Client portal</span>
            </Link>
            <nav className="flex items-center gap-5">
              <Link to="/portal" data-testid="portal-nav-dashboard" className={`font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-300 ${location.pathname === "/portal" ? "text-ember" : "text-stone2 hover:text-bone"}`}>
                <LayoutDashboard size={14} /> Dashboard
              </Link>
              <Link to="/portal/account" data-testid="portal-nav-account" className={`font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-300 ${location.pathname === "/portal/account" ? "text-ember" : "text-stone2 hover:text-bone"}`}>
                <UserRound size={14} /> Account
              </Link>
              <button onClick={logout} data-testid="portal-logout-btn" className="font-mono text-xs uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300 flex items-center gap-1.5">
                <LogOut size={14} /> Sign out
              </button>
            </nav>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
          <p data-testid="portal-confidentiality-banner" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre border border-ember/40 bg-surface1 px-4 py-2.5 mb-8">
            Confidential client environment · {account.organisation} · Activity is audit-logged
          </p>
          {children}
        </main>
      </div>
    </PortalCtx.Provider>
  );
}
